import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ClosetDoorsStandardService } from 'src/app/services/configurators-2d/applications/closet-doors-standard.service';
import { CurrentService } from 'src/app/services/current.service';
import { Door } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door';
import { DoorSection } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door-section';
import { FinishesSubStep } from '../../../finishes/models/finishes-sub-step.enum';
import { DoorOffsetCalculatorService } from '../door-offset-calculator.service';

/**
 * Preview de l'étape Finitions.
 * Affiche les vantaux avec leurs calques réels et permet la sélection interactive.
 * En sous-étape Décors, chaque section est cliquable : un clic sélectionne la section
 * et affiche les couleurs disponibles dans le panneau de droite.
 */
@Component({
  selector: 'ng-casto-closet-doors-standard-preview-finishes',
  templateUrl: './preview-step-finishes.component.html',
  styleUrls: ['./preview-step-finishes.component.scss'],
})
export class PreviewStepFinishesComponent implements OnChanges {
  /** Expose l'enum au template. */
  readonly SubStep = FinishesSubStep;

  /** Sous-étape active. */
  @Input() finishesStep: FinishesSubStep;

  /** Indique que le rendu initial est terminé — déclenche le focus sur le premier vantail. */
  @Input() finishesRendered: boolean = false;

  private readonly current = inject(CurrentService);
  private readonly closetDoorsService = inject(ClosetDoorsStandardService);
  private readonly doorOffsetCalculator = inject(DoorOffsetCalculatorService);

  /** Préfixe d'URL pour les images servies par l'API. */
  protected readonly baseImgURL = this.closetDoorsService.apiUrl;

  /** Liste des vantaux configurés. */
  protected get doors() {
    return this.current.closetDoors.doors;
  }

  /** Identifiant du vantail actuellement sélectionné. */
  protected get selectedDoorId() {
    return this.current.closetDoors.selectedDoorId;
  }

  /** Index de la section actuellement sélectionnée. */
  protected get selectedSectionIndex() {
    return this.current.closetDoors.selectedSectionIndex;
  }

  /** Nombre de vantaux — utilisé pour le calcul de l'offset de recouvrement. */
  protected get doorNumber() {
    return this.current.closetDoors.doorNumber;
  }

  /**
   * Sélectionne ou désélectionne un vantail.
   * Auto-sélectionne la première section configurable sur mobile (scroll vers le haut).
   */
  protected selectDoor(id: number): void {
    if (this.current.closetDoors.selectedDoorId === id) {
      this.current.closetDoors.selectedDoorId = undefined;
      this.current.closetDoors.selectedSectionIndex = undefined;
      return;
    }
    this.current.closetDoors.selectedDoorId = id;
    const door = this.doors.find(d => d.id === id);
    const firstConfigurable = door?.sections?.find((s: any) => !s.isFixed);
    this.current.closetDoors.selectedSectionIndex = firstConfigurable?.index;
    if (document.documentElement.clientWidth < 767) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Sélectionne une section dans la preview (sous-étape Décors uniquement).
   * Les sections fixes (IsFixed) ne sont pas sélectionnables.
   */
  protected selectSection(doorId: number, sectionIndex: number): void {
    const door = this.doors.find((d: any) => d.id === doorId);
    const section = door?.sections?.find((s: any) => s.index === sectionIndex);
    if (!section || section.isFixed) return;

    this.current.closetDoors.selectedDoorId = doorId;
    this.current.closetDoors.selectedSectionIndex = sectionIndex;
    if (document.documentElement.clientWidth < 767) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Retourne l'URL de background d'une section pour l'affichage CSS.
   * Section avec couleur → icône de la couleur. Sinon → null (fond transparent).
   */
  protected getSectionBackground(section: any): string | null {
    const color = section.color;
    if (!color?.icon) return null;
    return `url(${this.baseImgURL}${color.icon})`;
  }

  /**
   * `true` si la section est active : soit la section sélectionnée, soit une section
   * liée à la section sélectionnée (remplissage à l'identique). Tout le groupe lié s'allume
   * et ne montre donc pas le crayon, conformément à la règle de sélection groupée.
   */
  protected isActiveSection(doorId: number, sectionIndex: number): boolean {
    if (this.selectedDoorId !== doorId || this.selectedSectionIndex === undefined) return false;
    if (this.selectedSectionIndex === sectionIndex) return true;

    const selectedSection = this.doors
      .find((d: Door) => d.id === doorId)
      ?.sections?.find((s: DoorSection) => s.index === this.selectedSectionIndex);
    return selectedSection?.linkedSectionIndexes?.includes(sectionIndex) ?? false;
  }

  /**
   * Calcule la position verticale du centre d'une section (en % de la hauteur du vantail).
   * Utilisé pour positionner l'icône d'édition au milieu de la section.
   */
  protected getSectionMidpoint(door: any, section: any): number {
    const sections: any[] = door.sections ?? [];
    let cumulative = 0;
    for (const s of sections) {
      if (s.index === section.index) {
        return cumulative + s.heightPercentage / 2;
      }
      cumulative += s.heightPercentage;
    }
    return cumulative;
  }

  /** Indique si le vantail est en premier plan (recouvrement coulissant). */
  protected isInFront(id: number): boolean {
    const panel = this.getCurrentPanel(id);
    return panel !== null ? panel.orderFromFront === 1 : false;
  }

  /** Indique si le vantail a un décalage horizontal de recouvrement. */
  protected isOffset(id: number): boolean {
    const panel = this.getCurrentPanel(id);
    return panel !== null ? panel.isOffset : false;
  }

  /**
   * Retourne la valeur CSS `margin-left` pour le recouvrement coulissant.
   */
  protected calculateOffset(id: number, doorHtmlElement: HTMLElement): string {
    const door = this.doors.find(d => d.id === id);
    return `calc(-${this.doorOffsetCalculator.calculateOffset(door, this.doorNumber, doorHtmlElement)}px)`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['finishesRendered']?.currentValue === true) {
      document.getElementById('door1')?.focus();
    }
  }

  private getCurrentPanel(id: number): any {
    const allPanels = this.closetDoorsService.panelGroups.flatMap(g => g.panels);
    const panel = allPanels.find(p => p.panelCount === this.current.closetDoors.doorNumber);
    return panel?.panels?.find(p => p.index === id) ?? null;
  }
}
